/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import debounce from 'lodash/debounce';

import { Divider, breakpoints } from '@coursera/cds-core';

import { NARROW_XDP_CONTAINER_QUERY } from 'bundles/unified-description-page-common/constants/styles';

const originalStyles = {
  wrapper: (applyLightShadow?: boolean) => css`
    ${breakpoints.up('md')} {
      border: 1px solid var(--cds-color-grey-50);
      border-radius: var(--cds-spacing-100);
      margin: -76px auto 0;
      position: relative;
      background-color: var(--cds-color-neutral-background-primary);
      box-shadow: ${
        applyLightShadow
          ? '0 0 var(--cds-spacing-100) 2px var(--cds-color-darken-100)'
          : '0 var(--cds-spacing-100) var(--cds-spacing-200) 0 var(--cds-color-darken-200)'
      };
    }

    /* Strip the desktop card chrome so the wrapper renders flush like mobile
       when the chat-narrowed XDP column shows the mobile variant. */
    ${NARROW_XDP_CONTAINER_QUERY} {
      border: none;
      border-radius: 0;
      margin: 0;
      background-color: transparent;
      box-shadow: none;
    }
  `,
  // Render both variants and toggle via CSS so the chat-narrowed XDP column
  // can swap to mobile layout without depending on viewport-only <Hidden>.
  desktopVariant: css`
    ${breakpoints.up('md')} {
      display: flex;
      flex: 1 1 0px;
      align-items: stretch;
      padding: var(--cds-spacing-300) 0;
      justify-content: center;
    }
    ${breakpoints.down('sm')} {
      display: none;
    }
    ${NARROW_XDP_CONTAINER_QUERY} {
      display: none;
    }
  `,
  mobileVariant: css`
    ${breakpoints.up('md')} {
      display: none;
    }
    ${NARROW_XDP_CONTAINER_QUERY} {
      display: block;
    }
  `,
  desktopItem: css`
    flex-grow: 1;
    display: flex;
    padding: 0 var(--cds-spacing-100);

    & + & {
      border-left: 1px solid var(--cds-color-grey-50);
    }

    .cds-ContextualHelp-container {
      /* keeps subtitle CTAs on the same line for desktop */
      display: flex !important;

      .cds-ContextualHelp-helpToggleButton {
        bottom: -6px;
      }
    }
  `,
  itemWrapper: (maxWidth: number) => css`
    align-self: center;
    margin: 0 auto;
    min-width: 120px;
    max-width: ${maxWidth}px;
  `,
  mobileItem: css`
    & + & {
      margin-top: var(--cds-spacing-200);
    }
  `,
  mobileHorizontalDivider: css`
    width: 100%;
    left: 0;
    position: absolute;
    margin: var(--cds-spacing-300) 0;
    height: 2px;
  `,
};

const isCompactStyles = {
  wrapper: css`
    ${breakpoints.up('md')} {
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      border-radius: var(--cds-spacing-100);
      margin: 0;
      position: relative;
      background-color: var(--cds-color-neutral-background-primary);
      box-shadow: none;
    }

    ${NARROW_XDP_CONTAINER_QUERY} {
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      border-radius: var(--cds-spacing-100);
      background-color: var(--cds-color-neutral-background-primary);

      ${breakpoints.down('sm')} {
        border: none;
        border-radius: 0;
      }
    }
  `,
  desktopVariant: css`
    ${NARROW_XDP_CONTAINER_QUERY} {
      ${breakpoints.up('md')} {
        display: flex;
      }
      ${breakpoints.down('sm')} {
        display: none;
      }
    }
  `,
  mobileVariant: css`
    ${NARROW_XDP_CONTAINER_QUERY} {
      ${breakpoints.up('md')} {
        display: none;
      }
    }
  `,
  desktopItem: css`
    & + & {
      border-left: none;
    }
  `,
};

type Props = {
  keyInformationItems: React.ReactNode[];
  applyLightShadow?: boolean;
  disableMobileDivider?: boolean;
  isCompact?: boolean;
};

const KeyInformationHorizontalModule = ({
  keyInformationItems,
  applyLightShadow,
  disableMobileDivider,
  isCompact,
}: Props) => {
  const styles = originalStyles;
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const desktopItemMaxWidth = React.useRef<number>(200);

  const keyInfoItems = keyInformationItems.filter((item) => {
    return item !== false;
  });

  React.useEffect(() => {
    if (!keyInfoItems.length) return undefined;

    const adjustKeyInfoItemsMaxWidth = () => {
      if (wrapperRef.current) {
        const wrapperWidth = wrapperRef.current.clientWidth;
        const itemWidth = wrapperWidth / keyInfoItems.length;
        // To make fully use of space, set max width as previous 200px or 70% of item width, whichever is greater
        desktopItemMaxWidth.current = Math.max(Math.floor(itemWidth * 0.7), 200);
      }
    };
    const debouncedAdjustKeyInfoItemsMaxWidth = debounce(adjustKeyInfoItemsMaxWidth, 300);

    // Adjust max width on mount
    adjustKeyInfoItemsMaxWidth();

    // Adjust max width on resize
    if (window) {
      window.addEventListener('resize', debouncedAdjustKeyInfoItemsMaxWidth);
    }

    return () => {
      if (window) {
        window.removeEventListener('resize', debouncedAdjustKeyInfoItemsMaxWidth);
      }
    };
  }, [keyInfoItems.length]);

  if (!keyInfoItems.length) {
    return null;
  }

  const renderDesktopInfoItems = () => {
    return keyInfoItems.map((keyInfoItem, index) => (
      <div key={`key-info-${index}`} css={[styles.desktopItem, isCompact && isCompactStyles.desktopItem]}>
        <div css={styles.itemWrapper(desktopItemMaxWidth.current)}>{keyInfoItem}</div>
      </div>
    ));
  };

  const renderMobileInfoItems = () => {
    return keyInfoItems.map((keyInfoItem, index) => (
      <div key={`key-info-${index}`} css={styles.mobileItem}>
        {keyInfoItem}
        {index === keyInfoItems.length - 1 && !disableMobileDivider && (
          <Divider orientation="horizontal" css={styles.mobileHorizontalDivider} />
        )}
      </div>
    ));
  };

  return (
    <div css={[styles.wrapper(applyLightShadow), isCompact && isCompactStyles.wrapper]} ref={wrapperRef}>
      <div css={[styles.mobileVariant, isCompact && isCompactStyles.mobileVariant]}>{renderMobileInfoItems()}</div>
      <div css={[styles.desktopVariant, isCompact && isCompactStyles.desktopVariant]}>{renderDesktopInfoItems()}</div>
    </div>
  );
};

export default KeyInformationHorizontalModule;
